import React, { useState, useEffect, useCallback } from "react";
import { LeadService } from "@/services/lead.service";
import { QuoteService } from "@/services/quote.service";
import { toUiOpportunity } from "@/mappers/lead.mapper";
import { formatDisplayReference } from "@/data/opportunityTypes";
import type { Opportunity, StageChange } from "@/data/opportunityTypes";
import type { DbLead, DbLeadCreate, DbLeadUpdate } from "@/types/database";
import type { DbQuote } from "@/types/database";
import { toUuidOrNull } from "@/lib/uuid";
import type { ProjectConfiguration } from "@/data/types";
import type { DepositInfo, ContractDocument, QuoteScenario } from "@/data/quoteTypes";

interface LeadInput {
  primaryName: string;
  primaryPhone: string;
  primaryEmail: string;
  secondaryName?: string;
  secondaryPhone?: string;
  secondaryEmail?: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  projectType: string;
  leadSource?: string;
  leadSourceId?: string;
  assignedRep?: string;
  notes?: string;
}

function parseQuoteAddress(address: string | null): Pick<Opportunity, "address" | "city" | "state" | "zip"> {
  if (!address) {
    return { address: "", city: "", state: "", zip: "" };
  }

  const parts = address.split(",").map((part) => part.trim());
  const street = parts[0] ?? "";
  const city = parts[1] ?? "";
  const stateZip = parts[2] ?? "";
  const [state = "", zip = ""] = stateZip.split(/\s+/);

  return { address: street, city, state, zip };
}

function normalizeQuoteStageHistory(raw: DbQuote["stage_history"]): StageChange[] {
  if (!Array.isArray(raw)) return [];

  return raw.flatMap((item) => {
    if (!item || typeof item !== "object") return [];

    const record = item as Record<string, unknown>;
    if (
      typeof record.id !== "string" ||
      typeof record.toStage !== "string" ||
      typeof record.changedAt !== "string" ||
      typeof record.changedBy !== "string"
    ) {
      return [];
    }

    return [{
      id: record.id,
      fromStage: typeof record.fromStage === "string" ? record.fromStage as Opportunity["stage"] : null,
      toStage: record.toStage as Opportunity["stage"],
      changedAt: record.changedAt,
      changedBy: record.changedBy,
    }];
  });
}

function toProjectConfig(raw: DbQuote["project_config"]): ProjectConfiguration | undefined {
  if (!raw || typeof raw !== "object") return undefined;
  return raw as unknown as ProjectConfiguration;
}

function toDeposit(raw: DbQuote["deposit"]): DepositInfo | undefined {
  if (!raw || typeof raw !== "object") return undefined;
  return raw as unknown as DepositInfo;
}

function toContract(raw: DbQuote["contract"]): ContractDocument | undefined {
  if (!raw || typeof raw !== "object") return undefined;
  return raw as unknown as ContractDocument;
}

function toScenarios(raw: DbQuote["scenarios"]): QuoteScenario[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  return raw as unknown as QuoteScenario[];
}

function quoteToOpportunity(row: DbQuote): Opportunity {
  const parsedAddress = parseQuoteAddress(row.customer_address);
  const stageHistory = normalizeQuoteStageHistory(row.stage_history);

  return {
    quoteId: row.id,
    leadId: row.lead_id || "",
    id: row.lead_id || row.id,
    displayReference: formatDisplayReference(row.quote_number || row.quote_name || row.id),
    primaryName: row.customer_name || "",
    secondaryName: "",
    primaryPhone: row.customer_phone || "",
    secondaryPhone: "",
    primaryEmail: row.customer_email || "",
    secondaryEmail: "",
    ...parsedAddress,
    projectType: "Other",
    leadSource: row.lead_source || "",
    stage: row.status,
    stageHistory,
    assignedRep: row.created_by || "",
    ownershipHistory: [],
    activities: [],
    totalEstimate: row.total_estimate || 0,
    projectConfig: toProjectConfig(row.project_config),
    scopeOfWork: row.scope_of_work || undefined,
    deposit: toDeposit(row.deposit),
    contract: toContract(row.contract),
    scenarios: toScenarios(row.scenarios),
    pricingStatus: row.pricing_status as Opportunity["pricingStatus"],
    notes: row.notes || "",
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    appointmentTime: row.appointment_time || undefined,
    wonAt: row.won_at || undefined,
    lostAt: row.lost_at || undefined,
    lostReason: row.lost_reason || undefined,
    lockedAt: row.locked_at || undefined,
    lockedBy: row.locked_by || undefined,
    presentedAt: row.presented_at || undefined,
    linkedJobId: row.linked_job_id || undefined,
    linkedQuoteId: row.id,
    customerName: row.customer_name || "",
    customerPhone: row.customer_phone || "",
    customerEmail: row.customer_email || "",
    customerAddress: row.customer_address || "",
    createdBy: row.created_by || "",
    status: row.status,
    quoteName: row.quote_name || "",
    convertedJobId: row.linked_job_id || undefined,
    quote_number: row.quote_number || undefined,
  };
}

function mergeLeadAndQuoteOpportunities(leads: DbLead[], quotes: DbQuote[]): Opportunity[] {
  const merged = new Map<string, Opportunity>();

  // Build reverse index: quote UUID → lead UUID, for leads that store linked_quote_id
  // but whose quote row doesn't have lead_id set (data gap in either direction is handled).
  const quoteIdToLeadId = new Map<string, string>();
  leads.forEach((lead) => {
    if (lead.linked_quote_id) quoteIdToLeadId.set(lead.linked_quote_id, lead.id);
  });

  leads.forEach((lead) => {
    const opp = toUiOpportunity(lead);
    merged.set(opp.id, opp);
  });

  quotes.forEach((quote) => {
    // Resolve merge key: prefer quote.lead_id, fall back to reverse index via lead.linked_quote_id.
    const mergeKey = quote.lead_id || quoteIdToLeadId.get(quote.id);
    if (!mergeKey) return;

    const quoteOpp = quoteToOpportunity(quote);
    const existing = merged.get(mergeKey);

    if (!existing) return;

    merged.set(mergeKey, {
      ...existing,
      ...quoteOpp,
      id: existing.id,
      displayReference: quoteOpp.displayReference || existing.displayReference,
      primaryName: existing.primaryName || quoteOpp.primaryName,
      primaryPhone: existing.primaryPhone || quoteOpp.primaryPhone,
      primaryEmail: existing.primaryEmail || quoteOpp.primaryEmail,
      address: existing.address || quoteOpp.address,
      city: existing.city || quoteOpp.city,
      state: existing.state || quoteOpp.state,
      zip: existing.zip || quoteOpp.zip,
      projectType: existing.projectType || quoteOpp.projectType,
      leadSource: existing.leadSource || quoteOpp.leadSource,
      assignedRep: existing.assignedRep || quoteOpp.assignedRep,
      appointmentTime: quoteOpp.appointmentTime ?? existing.appointmentTime,
      stageHistory: existing.stageHistory,
      ownershipHistory: existing.ownershipHistory,
      activities: existing.activities,
      // Prefer quote's normalized value (JOB-XXX) over lead's raw UUID
      linkedJobId: quoteOpp.linkedJobId || existing.linkedJobId,
      linkedQuoteId: quote.id,
      quoteName: quoteOpp.quoteName || existing.quoteName,
    });
  });

  return Array.from(merged.values()).sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
}

function toDbLeadInput(input: LeadInput): Partial<DbLeadCreate> & {
  primary_name: string;
  primary_phone: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  project_type: string;
} {
  return {
    primary_name: input.primaryName,
    primary_phone: input.primaryPhone,
    primary_email: input.primaryEmail || null,
    address: input.address,
    city: input.city,
    state: input.state,
    zip: input.zip,
    project_type: input.projectType,
    lead_source: input.leadSource || null,
    lead_source_id: input.leadSourceId || null,
    provider: "manual",
    assigned_rep: toUuidOrNull(input.assignedRep),
    notes: input.notes || null,
    organization_id: "",
    secondary_name: input.secondaryName || null,
    secondary_phone: input.secondaryPhone || null,
    secondary_email: input.secondaryEmail || null,
  };
}

export interface UseLeadsResult {
  leads: Opportunity[];
  loading: boolean;
  error: Error | null;
  setLeads: React.Dispatch<React.SetStateAction<Opportunity[]>>;
  createLead: (data: LeadInput) => Promise<Opportunity | null>;
  updateLead: (id: string, data: Partial<LeadInput>) => Promise<Opportunity | null>;
  deleteLead: (id: string) => Promise<boolean>;
  refresh: () => Promise<void>;
}

export interface UseLeadsOptions {
  enabled?: boolean;
}

export function useLeads(options: UseLeadsOptions = {}): UseLeadsResult {
  const enabled = options.enabled !== false;
  const [leads, setLeads] = useState<Opportunity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchLeads = useCallback(async () => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);

      const [leadResult, quoteResult] = await Promise.all([
        LeadService.getAll(),
        QuoteService.getAll(),
      ]);

      if (leadResult.error) {
        console.warn("Failed to load leads:", leadResult.error);
        setError(leadResult.error instanceof Error ? leadResult.error : new Error("Failed to load leads"));
        setLoading(false);
        return;
      }
      if (quoteResult.error) {
        console.warn("Failed to load quotes:", quoteResult.error);
        // Don't set error for quote failures - still show leads if we have them
      }

      const leadRows = leadResult.data ?? [];
      const quoteRows = quoteResult.data ?? [];

      const merged = mergeLeadAndQuoteOpportunities(leadRows, quoteRows);
      setLeads(merged);
    } catch (err) {
      const error = err instanceof Error ? err : new Error("Failed to load leads");
      setError(error);
      console.error("Failed to load leads:", err);
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    fetchLeads();
  }, [enabled, fetchLeads]);

  const createLead = useCallback(async (input: LeadInput): Promise<Opportunity | null> => {
    try {
      const dbInput = toDbLeadInput(input);
      const { data, error } = await LeadService.create(dbInput);

      if (error || !data) {
        console.error("Failed to create lead:", error);
        return null;
      }

      const newLead = toUiOpportunity(data);
      
      setLeads(prev => [newLead, ...prev]);
      
      return newLead;
    } catch (err) {
      console.error("Failed to create lead:", err);
      return null;
    }
  }, []);

  const updateLead = useCallback(async (id: string, input: Partial<LeadInput>): Promise<Opportunity | null> => {
    try {
      const updates: DbLeadUpdate = {};
      
      if (input.primaryName !== undefined) {
        updates.primary_name = input.primaryName;
      }
      if (input.primaryPhone !== undefined) updates.primary_phone = input.primaryPhone;
      if (input.primaryEmail !== undefined) updates.primary_email = input.primaryEmail || null;
      if (input.address !== undefined) updates.address = input.address;
      if (input.city !== undefined) updates.city = input.city;
      if (input.state !== undefined) updates.state = input.state;
      if (input.zip !== undefined) updates.zip = input.zip;
      if (input.projectType !== undefined) updates.project_type = input.projectType;
      if (input.leadSource !== undefined) updates.lead_source = input.leadSource || null;
      if (input.assignedRep !== undefined) updates.assigned_rep = toUuidOrNull(input.assignedRep);
      if (input.notes !== undefined) updates.notes = input.notes || null;
      if (input.secondaryName !== undefined) updates.secondary_name = input.secondaryName || null;
      if (input.secondaryPhone !== undefined) updates.secondary_phone = input.secondaryPhone || null;
      if (input.secondaryEmail !== undefined) updates.secondary_email = input.secondaryEmail || null;

      const { data, error } = await LeadService.update(id, updates);

      if (error || !data) {
        console.error("Failed to update lead:", error);
        return null;
      }

      const updatedLead = toUiOpportunity(data);
      
      setLeads(prev => prev.map(lead => 
        lead.id === id ? { ...lead, ...updatedLead } : lead
      ));
      
      return updatedLead;
    } catch (err) {
      console.error("Failed to update lead:", err);
      return null;
    }
  }, []);

  const deleteLead = useCallback(async (id: string): Promise<boolean> => {
    try {
      const { error } = await LeadService.delete(id);

      if (error) {
        console.error("Failed to delete lead:", error);
        return false;
      }

      setLeads(prev => prev.filter(lead => lead.id !== id));
      
      return true;
    } catch (err) {
      console.error("Failed to delete lead:", err);
      return false;
    }
  }, []);

  const refresh = useCallback(async () => {
    await fetchLeads();
  }, [fetchLeads]);

  return {
    leads,
    loading,
    error,
    setLeads,
    createLead,
    updateLead,
    deleteLead,
    refresh,
  };
}
